import { useAuth } from './auth';
import i18n from './i18n';
import type { TradeKey } from './schemas';

/* ---------------------------------------------------------------------------
   מה העסק רואה במערכת — עותק של src/common/features.ts בשרת. התפריט,
   המסכים וההגדרות נגזרים מזה. זה לא הרשאה (ההרשאות לפי תפקיד נאכפות בשרת).
   --------------------------------------------------------------------------- */

export const FEATURE_KEYS = [
  'customers',
  'quotes',
  'priceList',
  'invoicing',
  'scheduling',
  'inventory',
  'equipment',
  'suppliers',
  'purchaseOrders',
  'supplierInvoices',
  'contracts',
] as const;
export type Feature = (typeof FEATURE_KEYS)[number];

// getters: התווית וההסבר בשפה הנוכחית (common:features.<key>.*), והצורה לא משתנה.
export const FEATURES = Object.fromEntries(
  FEATURE_KEYS.map((f) => [
    f,
    {
      get label() {
        return i18n.t(`common:features.${f}.label`);
      },
      get hint() {
        return i18n.t(`common:features.${f}.hint`);
      },
    },
  ]),
) as Record<Feature, { label: string; hint: string }>;

const REQUIRES: Partial<Record<Feature, Feature[]>> = {
  quotes: ['priceList', 'customers'],
  invoicing: ['priceList', 'customers'],
  equipment: ['customers'],
  purchaseOrders: ['suppliers'],
  supplierInvoices: ['suppliers'],
};

const SERVICE_BUSINESS: Feature[] = ['customers', 'quotes', 'priceList', 'invoicing', 'scheduling'];

export const DEFAULT_FEATURES: Record<TradeKey, Feature[]> = {
  HVAC: [...SERVICE_BUSINESS, 'equipment', 'inventory'],
  ELECTRICAL: [...SERVICE_BUSINESS, 'inventory'],
  PLUMBING: [...SERVICE_BUSINESS, 'inventory'],
  CARPENTRY: [...SERVICE_BUSINESS, 'inventory', 'suppliers', 'purchaseOrders'],
  RENOVATION: [...SERVICE_BUSINESS, 'suppliers', 'purchaseOrders'],
  BUILDING_MAINTENANCE: ['customers', 'priceList', 'invoicing', 'scheduling', 'suppliers', 'purchaseOrders', 'supplierInvoices', 'contracts'],
  CLEANING: SERVICE_BUSINESS,
  GARDENING: SERVICE_BUSINESS,
  RETAIL: ['customers', 'priceList', 'invoicing', 'inventory', 'suppliers', 'purchaseOrders', 'supplierInvoices'],
  OTHER: SERVICE_BUSINESS,
  OFFICE: ['suppliers', 'purchaseOrders', 'supplierInvoices', 'contracts'],
};

/** משלים תלויות (הצעות מחיר → מחירון ולקוחות), בסדר קבוע. */
export function normalizeFeatures(raw: readonly string[]): Feature[] {
  const set = new Set(raw.filter((f): f is Feature => (FEATURE_KEYS as readonly string[]).includes(f)));
  for (const f of [...set]) for (const dep of REQUIRES[f] ?? []) set.add(dep);
  return FEATURE_KEYS.filter((f) => set.has(f));
}

/** מה נדרש בגלל מודול אחר שדלוק — כדי להסביר בממשק למה אי אפשר לכבות. */
export function requiredBy(feature: Feature, selected: readonly Feature[]): Feature | null {
  return selected.find((f) => f !== feature && (REQUIRES[f] ?? []).includes(feature)) ?? null;
}

export function useFeatures(): { has: (f: Feature) => boolean; list: Feature[] } {
  const { session } = useAuth();
  const trade = session?.tenant?.trade ?? 'OTHER';
  const list = session?.tenant?.features ? normalizeFeatures(session.tenant.features) : normalizeFeatures(DEFAULT_FEATURES[trade]);
  return { list, has: (f) => list.includes(f) };
}
